import { TextField, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { PutProductDistributorFulfillmentType } from "../../ProductSettings/hooks/useFetchProductSettingsDistributorOverrides";
import MenuItem from '@mui/material/MenuItem';
import { useDistributorSettingsContext } from "../context/DistributorSettingsContext";
import PropTypes from 'prop-types';
import { SkuFulfillmentMethod } from "../../../Enums/SkuFulfillmentMethod";
import { SkuOfferingStatus } from "../../../Enums/SkuOfferingStatus";
import DirectDistributorDialog from "../../ProductSettings/dialogs/DirectDistributorDialog";
import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton } from '@mui/material';
import { CustomerType } from "../../../Enums/CustomerType";

export const SkuFulfillmentTypeSelect = ({
    handleUpdateRow, isChanged, row, originalRow
}) => {
    const { customerType, getPossibleSkuFulfillmentTypes } = useDistributorSettingsContext();
    const [showDirectDistributorDialog, setShowDirectDistributorDialog] = useState(false);
    const [pendingFulfillmentType, setPendingFulfillmentType] = useState(null);
    const [isChangingDirectDistributor, setIsChangingDirectDistributor] = useState(false);

    const updateProductDistributorFulfillmentType = async (skuRow, originalRow) => {
        if (isChanged(skuRow, originalRow)) {
            // The PUT helper reads `.id` as the distributor id; our rows key `.id` by sku.
            await PutProductDistributorFulfillmentType(skuRow.product, { ...skuRow, id: skuRow.distributorId });
        }
    };

    const handleFulfillmentTypeChange = async (event) => {
        const newFulfillmentType = getPossibleSkuFulfillmentTypes(customerType, row.skuOfferingStatus?.id)?.skuFulfillmentTypes.find(status => status.id === event.target.value);

        if (newFulfillmentType?.id === SkuFulfillmentMethod.INDIRECT) {
            setPendingFulfillmentType(newFulfillmentType);
            setIsChangingDirectDistributor(false);
            setShowDirectDistributorDialog(true);
        } else {
            const tempRow = {
                ...row,
                skuFulfillmentType: newFulfillmentType
            };

            await handleUpdateRow(tempRow);
            await updateProductDistributorFulfillmentType(tempRow, originalRow);
        }
    };

    const handleDirectDistributorSave = async () => {
        const tempRow = {
            ...row,
            skuFulfillmentType: pendingFulfillmentType
        };

        await handleUpdateRow(tempRow);
    };

    const handleChangeDirectDistributorSave = async (newDirectDistributor) => {
        const tempRow = {
            ...row,
            iIndirectDistributorId: newDirectDistributor.distributorId,
            indirectDistributorName: newDirectDistributor.name,
            indirectDistributorSapId: newDirectDistributor.sapId,
            skuFulfillmentType: row.skuFulfillmentType
        };

        await handleUpdateRow(tempRow);
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    id="outlined-select-fulfillment"
                    select
                    value={row.skuFulfillmentType?.id ?? SkuFulfillmentMethod.REMOVED}
                    size="small"
                    fullWidth
                    onChange={handleFulfillmentTypeChange}
                >
                    {(() => {
                        const possibleTypes = getPossibleSkuFulfillmentTypes(
                            customerType ?? CustomerType.VMI,
                            row.skuOfferingStatus?.id ?? SkuOfferingStatus.NOT_OFFERED
                        );
                        return possibleTypes?.skuFulfillmentTypes?.map(status =>
                            <MenuItem key={status.id}
                                value={status.id}>
                                {status.description}
                            </MenuItem>) || [];
                    })()}
                </TextField>

                {row.skuFulfillmentType?.id === SkuFulfillmentMethod.INDIRECT && row.indirectDistributorName && (
                    <Tooltip
                        title={`Indirect Distributor: ${row.indirectDistributorSapId} - ${row.indirectDistributorName}. Click to change.`}
                        arrow
                        placement="top"
                    >
                        <IconButton
                            onClick={() => {
                                setIsChangingDirectDistributor(true);
                                setShowDirectDistributorDialog(true);
                            }}
                            size="small"
                            sx={{
                                color: 'primary.main',
                                padding: '4px'
                            }}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <DirectDistributorDialog
                open={showDirectDistributorDialog}
                onClose={() => {
                    setShowDirectDistributorDialog(false);
                    setIsChangingDirectDistributor(false);
                    setPendingFulfillmentType(null);
                }}
                onSave={isChangingDirectDistributor ? handleChangeDirectDistributorSave : handleDirectDistributorSave}
                currentDistributor={row}
                sku={row.product?.sku}
                isChanging={isChangingDirectDistributor}
            />
        </>
    );
};

SkuFulfillmentTypeSelect.propTypes = {
    handleUpdateRow: PropTypes.func,
    isChanged: PropTypes.func,
    row: PropTypes.object,
    originalRow: PropTypes.object,
};
